import * as types from '../constants/actionTypes';
import flatten from 'lodash/flatten';
import zip from 'lodash/zip';
import groupBy from 'lodash/groupBy';
import map from 'lodash/map';
import flow from 'lodash/flow';
import mapValues from 'lodash/mapValues';
import partialRight from 'lodash/partialRight';
import clone from 'lodash/clone';
import ambassadorsReducer from '../pages/EditProfile/reducers/AmbassadorReducer';
import videoReducer from '../pages/EditProfile/reducers/videoReducer';
import imageReducer from '../pages/EditProfile/reducers/imageReducer';
import followCompanyReducer from '../pages/EditProfile/reducers/followCompanyReducer.js';
import validateProfile from '../../shared/validateProfile';
import {
    prepareOccupation,
    prepareOccupations,
} from '../utils/occupation-utils';

function validateProfileAndReturnState(newState) {
    return Object.assign({}, newState, {
        profileErrors: validateProfile(newState.profile),
    });
}

function prepareImageOccupations(profile) {
    return {
        ...profile,
        images: {
            ...profile.images,
            images: profile.images.images.map((image) => ({
                ...image,
                occupations: prepareOccupations(image.occupations),
            })),
        },
    };
}

const prepareSpecialisations = flow(
    partialRight(groupBy, 'specialization'),
    partialRight(map, (s) => ({ ...s[0], count: s.length }))
);

export const extractOccupationsFromAds = flow(
    partialRight(map, (ad) => ad.occupations),
    flatten,
    partialRight(map, ([general, specialization]) =>
        prepareOccupation({ general, specialization })
    ),
    partialRight(groupBy, 'general'),
    partialRight(mapValues, (specializations) => ({
        count: specializations.length,
        specializations: prepareSpecialisations(specializations),
    })),
    partialRight(map, ({ specializations, count }, general) => [
        { general, count },
        specializations,
    ]),
    (arr) => zip(...arr),
    ([general, specializations]) => ({
        general,
        specializations: flatten(specializations),
    })
);

const initialState = {
    loading: false,
    loaded: false,
    companyActiveAds: {
        filter: '',
        occupations: {
            general: [],
            specializations: [],
        },
        ads: [],
    },
    profile: {
        companyAddress: '',
    },
    profileErrors: {},
    error: null,
    followCompany: {},
    loadingProfileLogo: false,
    loadingProfileBackground: false,
    isSaving: false,
};

export default function reducer(state = initialState, action = {}) {
    switch (action.type) {
        case types.FETCH_PROFILE:
        case types.FETCH_EDITABLE_PROFILE:
            return {
                ...state,
                loading: true,
            };
        case types.FETCH_PROFILE_SUCCESS:
        case types.FETCH_EDITABLE_PROFILE_SUCCESS:
            return validateProfileAndReturnState({
                ...state,
                loading: false,
                loaded: true,
                profile: {
                    ...state.profile,
                    ...prepareImageOccupations(action.result),
                },

                // used in publish/unpublish profile
                isSaving: false,
            });
        case types.FETCH_PROFILE_FAIL:
        case types.FETCH_EDITABLE_PROFILE_FAIL:
            return {
                ...state,
                loading: false,
                loaded: false,
                error: action.error,
                isSaving: false,
            };

        // fetch company ads
        case types.FETCH_COMPANY_ADS:
            return state;
        case types.FETCH_COMPANY_ADS_SUCCESS:
            return {
                ...state,
                companyActiveAds: {
                    ...state.companyActiveAds,
                    occupations: extractOccupationsFromAds(action.result),
                    ads: action.result,
                },
            };
        case types.FETCH_COMPANY_ADS_FAIL:
            return {
                ...state,
                companyActiveAds: {
                    ...state.companyActiveAds,
                    error: action.error,
                },
            };
        case types.FILTER_COMPANY_ADS:
            return {
                ...state,
                companyActiveAds: {
                    ...state.companyActiveAds,
                    filter: action.filter,
                },
            };

        // upload profile background image
        case types.UPLOAD_PROFILE_BACKGROUND:
            return {
                ...state,
                loadingProfileBackground: true,
            };

        // VALIDATE
        case types.UPLOAD_PROFILE_BACKGROUND_SUCCESS:
            return validateProfileAndReturnState({
                ...state,
                profile: {
                    ...clone(state.profile),
                    background: action.result.value,
                },
                loadingProfileBackground: false,
            });

        case types.UPLOAD_PROFILE_BACKGROUND_FAIL:
            return {
                ...state,
                error: action.error,
                loadingProfileBackground: false,
            };

        // upload profile logo image
        case types.UPLOAD_PROFILE_LOGO:
            return {
                ...state,
                loadingProfileLogo: true,
            };

        // VALIDATES
        case types.UPLOAD_PROFILE_LOGO_SUCCESS:
            return validateProfileAndReturnState({
                ...state,
                profile: {
                    ...clone(state.profile),
                    logo: action.result.value,
                },
                loadingProfileLogo: false,
            });

        case types.UPLOAD_PROFILE_LOGO_FAIL:
            return {
                ...state,
                error: action.error,
                loadingProfileLogo: false,
            };

        // Update profile fields
        case types.UPDATE_FIELD:
            return {
                ...state,
                loading: true,
            };

        // VALIDATE
        case types.UPDATE_FIELD_SUCCESS:
            return validateProfileAndReturnState({
                ...state,
                profile: {
                    ...state.profile,
                    [action.meta.name]: action.meta.value,
                },
                loading: false,
            });
        case types.UPDATE_FIELD_FAIL:
            return {
                ...state,
                error: action.error,
                loading: false,
            };

        case types.PROFILE_SET_ERRORS:
            return {
                ...state,
                profileErrors: action.error,
            };

        // Request Publish
        case types.PUBLISH_PROFILE:
            return {
                ...state,
                isSaving: true,
            };
        case types.PUBLISH_PROFILE_SUCCESS:
            return {
                ...state,
                profile: {
                    ...state.profile,
                },
            };
        case types.PUBLISH_PROFILE_FAIL:
            return {
                ...state,
                error: action.error,
                isSaving: false,
            };

        // Unpublish
        case types.UNPUBLISH_PROFILE:
            return {
                ...state,
                isSaving: true,
            };
        case types.UNPUBLISH_PROFILE_SUCCESS:
            return {
                ...state,
                profile: {
                    ...state.profile,
                },
            };
        case types.UNPUBLISH_PROFILE_FAIL:
            return {
                ...state,
                error: action.error,
                isSaving: false,
            };

        // Ambassador
        case types.ADD_AMBASSADOR:
        case types.ADD_AMBASSADOR_SUCCESS:
        case types.ADD_AMBASSADOR_FAIL:
        case types.DELETE_AMBASSADOR:
        case types.DELETE_AMBASSADOR_SUCCESS:
        case types.DELETE_AMBASSADOR_FAIL:
        case types.EDIT_AMBASSADOR:
        case types.EDIT_AMBASSADOR_SUCCESS:
        case types.EDIT_AMBASSADOR_FAIL:
        case types.UPDATE_AMBASSADOR_IMAGE:
        case types.UPDATE_AMBASSADOR_IMAGE_SUCCESS:
        case types.UPDATE_AMBASSADOR_IMAGE_FAIL: {
            const newProfile = Object.assign({}, state.profile);
            const ambassadors = {
                ambassadors: ambassadorsReducer(newProfile.ambassadors, action),
            };
            Object.assign(newProfile, ambassadors);

            return {
                ...state,
                profile: newProfile,
            };
        }

        // Video
        case types.ADD_VIDEO:
        case types.ADD_VIDEO_FAIL:
        case types.ADD_VIDEO_SUCCESS:
        case types.DELETE_VIDEO:
        case types.DELETE_VIDEO_FAIL:
        case types.DELETE_VIDEO_SUCCESS:
        case types.UPDATE_VIDEO:
        case types.UPDATE_VIDEO_FAIL:
        case types.UPDATE_VIDEO_SUCCESS: {
            const newProfile = Object.assign({}, state.profile);
            const videos = { videos: videoReducer(newProfile.videos, action) };
            Object.assign(newProfile, videos);

            return {
                ...state,
                profile: newProfile,
            };
        }

        // Image
        case types.UPLOAD_IMAGE:
        case types.UPLOAD_IMAGE_FAIL:
        case types.UPLOAD_IMAGE_SUCCESS:
        case types.UPDATE_IMAGE:
        case types.UPDATE_IMAGE_FAIL:
        case types.UPDATE_IMAGE_SUCCESS:
        case types.REMOVE_IMAGE:
        case types.REMOVE_IMAGE_FAIL:
        case types.REMOVE_IMAGE_SUCCESS:
        case types.REORDER_IMAGE:
        case types.REORDER_IMAGE_FAIL:
        case types.REORDER_IMAGE_SUCCESS: {
            const newProfile = Object.assign({}, state.profile);
            const images = { images: imageReducer(newProfile.images, action) };
            Object.assign(newProfile, images);

            return {
                ...state,
                profile: newProfile,
            };
        }
        case types.FETCH_FOLLOWER_DETAILS:
        case types.FETCH_FOLLOWER_DETAILS_FAIL:
        case types.FETCH_FOLLOWER_DETAILS_SUCCESS:
        case types.FOLLOW_COMPANY:
        case types.FOLLOW_COMPANY_FAIL:
        case types.FOLLOW_COMPANY_SUCCESS:
        case types.UNFOLLOW_COMPANY:
        case types.UNFOLLOW_COMPANY_FAIL:
        case types.UNFOLLOW_COMPANY_SUCCESS: {
            return {
                ...state,
                followCompany: followCompanyReducer(
                    state.followCompany,
                    action
                ),
            };
        }
        default:
            return state;
    }
}
